import React, { useState } from 'react';
import { PageHeader } from '@/components/page-header';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AddGoalSheet } from '@/components/goals/add-goal-sheet';
import { useAppStore } from '@/lib/data';
import { formatCurrency, getCurrentCurrencySymbol } from '@/lib/utils';
import {
  PiggyBank, Target, Users, Trash2, Plus, Minus,
  TrendingUp, TrendingDown, Clock,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import type { SavingsGoal } from '@/lib/types';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from '@/components/ui/dialog';
import { differenceInDays, format, parseISO } from 'date-fns';

// Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ On-track calculation Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
function getOnTrackStatus(goal: SavingsGoal) {
  const createdAt = goal.createdAt ? parseISO(goal.createdAt) : new Date();
  const deadline = parseISO(goal.deadline);
  const today = new Date();
  const totalDays = Math.max(1, differenceInDays(deadline, createdAt));
  const elapsed = Math.max(0, differenceInDays(today, createdAt));
  const daysLeft = Math.max(0, differenceInDays(deadline, today));
  const expectedAmount = (elapsed / totalDays) * goal.targetAmount;
  const progress = goal.targetAmount > 0 ? (goal.currentAmount / goal.targetAmount) * 100 : 0;

  if (progress >= 100) return { status: 'complete', label: 'Complete!', color: 'emerald', daysLeft };
  if (daysLeft === 0) return { status: 'overdue', label: 'Overdue', color: 'red', daysLeft };
  if (goal.currentAmount >= expectedAmount) return { status: 'on-track', label: 'On Track', color: 'emerald', daysLeft };
  if (goal.currentAmount >= expectedAmount * 0.8) return { status: 'slightly-behind', label: 'Slightly Behind', color: 'amber', daysLeft };
  return { status: 'behind', label: 'Behind', color: 'red', daysLeft };
}

// Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Contribution Dialog Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
function ContributeDialog({ goal, open, onOpenChange }: { goal: SavingsGoal; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { addSavingsContribution } = useAppStore();
  const { toast } = useToast();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [type, setType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (!num || num <= 0) return;
    setLoading(true);
    try {
      await addSavingsContribution({
        goalId: goal.id,
        amount: type === 'deposit' ? num : -num,
        note: note.trim() || undefined,
        date: new Date().toISOString(),
      });
      toast({
        title: type === 'deposit' ? 'Deposit recorded' : 'Withdrawal recorded',
        description: `${formatCurrency(num)} ${type === 'deposit' ? 'added to' : 'removed from'} "${goal.name}"`,
      });
      setAmount('');
      setNote('');
      onOpenChange(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to record contribution.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Log Contribution Ã¢â‚¬â€ {goal.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Deposit / Withdrawal toggle */}
          <div className="flex rounded-xl overflow-hidden border border-border">
            <button
              type="button"
              onClick={() => setType('deposit')}
              className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                type === 'deposit' ? 'bg-emerald-500 text-white' : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              <TrendingUp className="h-4 w-4" /> Deposit
            </button>
            <button
              type="button"
              onClick={() => setType('withdrawal')}
              className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                type === 'withdrawal' ? 'bg-rose-500 text-white' : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              <TrendingDown className="h-4 w-4" /> Withdrawal
            </button>
          </div>

          <div className="space-y-1.5">
            <Label>Amount ({getCurrentCurrencySymbol()})</Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="text-lg font-semibold"
              autoFocus
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              placeholder="e.g. Monthly transfer, bonus..."
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={loading || !amount}
              className={type === 'deposit' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'}
            >
              {loading ? 'Saving...' : `Record ${type === 'deposit' ? 'Deposit' : 'Withdrawal'}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Contribution History Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
function ContributionHistory({ goalId }: { goalId: string }) {
  const { savingsContributions, deleteSavingsContribution } = useAppStore();
  const { toast } = useToast();
  const contributions = savingsContributions
    .filter(c => c.goalId === goalId)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (contributions.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">No contributions yet. Log your first deposit!</p>;
  }

  return (
    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
      {contributions.map(c => (
        <div key={c.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
          <div className="flex items-center gap-3">
            <div className={`p-1.5 rounded-full ${c.amount > 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
              {c.amount > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            </div>
            <div>
              <p className={`text-sm font-semibold ${c.amount > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {c.amount > 0 ? '+' : ''}{formatCurrency(c.amount)}
              </p>
              {c.note && <p className="text-xs text-muted-foreground">{c.note}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{format(parseISO(c.date), 'MMM d')}</span>
            <button
              onClick={async () => {
                await deleteSavingsContribution(c.id, c.goalId, c.amount);
                toast({ title: 'Entry removed' });
              }}
              className="text-muted-foreground hover:text-destructive transition-colors p-1"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Goal Card Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
function GoalCard({ goal, onDelete }: { goal: SavingsGoal; onDelete: () => void }) {
  const [contributeOpen, setContributeOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const { savingsContributions } = useAppStore();

  const progress = goal.targetAmount > 0 ? Math.min(100, (goal.currentAmount / goal.targetAmount) * 100) : 0;
  const remaining = Math.max(0, goal.targetAmount - goal.currentAmount);
  const { status, label, color, daysLeft } = getOnTrackStatus(goal);
  const contributionCount = savingsContributions.filter(c => c.goalId === goal.id).length;

  const statusColors: Record<string, string> = {
    complete: 'bg-emerald-100 text-emerald-700',
    'on-track': 'bg-emerald-100 text-emerald-700',
    'slightly-behind': 'bg-amber-100 text-amber-700',
    behind: 'bg-rose-100 text-rose-700',
    overdue: 'bg-rose-100 text-rose-700',
  };

  const barColors: Record<string, string> = {
    complete: 'bg-emerald-500',
    'on-track': 'bg-emerald-500',
    'slightly-behind': 'bg-amber-500',
    behind: 'bg-rose-500',
    overdue: 'bg-rose-500',
  };

  return (
    <>
      <Card className="flex flex-col rounded-2xl shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-xl bg-blue-50 shrink-0">
                <PiggyBank className="h-5 w-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base truncate">{goal.name}</CardTitle>
                <CardDescription className="flex items-center gap-1 mt-0.5">
                  <Clock className="h-3 w-3" />
                  {daysLeft > 0 ? `${daysLeft} days left` : 'Deadline passed'}
                </CardDescription>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ${statusColors[status]}`}>
              {label}
            </span>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 flex-1">
          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-bold">{Math.round(progress)}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-700 ${barColors[status]}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-muted-foreground">Saved</p>
              <p className="text-sm font-bold text-emerald-600 mt-0.5">{formatCurrency(goal.currentAmount)}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-muted-foreground">Remaining</p>
              <p className="text-sm font-bold mt-0.5">{formatCurrency(remaining)}</p>
            </div>
          </div>

          {/* Monthly target hint */}
          {goal.monthlyTarget && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Target: {formatCurrency(goal.monthlyTarget)}/month
            </p>
          )}

          {/* History toggle */}
          <button
            onClick={() => setHistoryOpen(v => !v)}
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            <span>{contributionCount} contribution{contributionCount !== 1 ? 's' : ''}</span>
            {historyOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>

          {historyOpen && <ContributionHistory goalId={goal.id} />}
        </CardContent>

        <CardFooter className="flex justify-between gap-2 pt-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            onClick={() => setContributeOpen(true)}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Log Contribution
          </Button>
        </CardFooter>
      </Card>

      <ContributeDialog goal={goal} open={contributeOpen} onOpenChange={setContributeOpen} />
    </>
  );
}

// Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Page Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
export default function SavingsPage() {
  const { savingsGoals, deleteSavingsGoal } = useAppStore();
  const { toast } = useToast();

  const personalGoals = savingsGoals.filter(g => g.type === 'individual');
  const totalTarget = personalGoals.reduce((s, g) => s + g.targetAmount, 0);
  const totalSaved = personalGoals.reduce((s, g) => s + g.currentAmount, 0);
  const overallProgress = totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0;

  const handleDelete = async (id: string, name: string) => {
    await deleteSavingsGoal(id);
    toast({ title: 'Goal deleted', description: `"${name}" has been removed.` });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container-padding space-y-6 pb-8">
        <PageHeader
          title="Savings"
          description="Track your personal savings goals and log contributions."
          icon={<Target className="h-6 w-6 text-blue-600" />}
        >
          <div className="flex gap-2">
            <AddGoalSheet />
            <Button asChild variant="outline">
              <Link to="/dashboard/savings/groups">
                <Users className="h-4 w-4 mr-2" />
                Group Savings
              </Link>
            </Button>
          </div>
        </PageHeader>

        {/* Summary */}
        {personalGoals.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="rounded-2xl shadow-sm">
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-xs text-muted-foreground uppercase font-bold tracking-wide">Total Target</p>
                <p className="text-xl font-black mt-1">{formatCurrency(totalTarget)}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl shadow-sm">
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-xs text-muted-foreground uppercase font-bold tracking-wide">Total Saved</p>
                <p className="text-xl font-black mt-1 text-emerald-600">{formatCurrency(totalSaved)}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl shadow-sm">
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-xs text-muted-foreground uppercase font-bold tracking-wide">Overall</p>
                <p className="text-xl font-black mt-1">{Math.round(overallProgress)}%</p>
              </CardContent>
            </Card>
          </div>
        )}

        {personalGoals.length > 0 ? (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {personalGoals.map(goal => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onDelete={() => handleDelete(goal.id, goal.name)}
              />
            ))}
          </div>
        ) : (
          <Card className="text-center py-14 rounded-2xl shadow-sm">
            <CardContent>
              <PiggyBank className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Savings Goals Yet</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">
                Create a goal to start tracking your progress and log deposits over time.
              </p>
              <div className="mt-6">
                <AddGoalSheet />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
